import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';

import { Box } from '../../Box';
import { TextButton, TextButtonProps } from '../../Button';
import { Text } from '../../Text';

const { formLayout: formLayoutTokens } = tokens;

export type TopNavProps =
  | {
      /**
       * Optional label for the header (only rendered if provided)
       */
      navLabel?: string;
      /**
       * Optional props for the text button (only rendered if provided)
       */
      textButtonProps?: Omit<TextButtonProps, 'size' | 'variant'> &
        Partial<Pick<TextButtonProps, 'size' | 'variant'>>;
    }
  | {
      /**
       * Children to render in the header. Only use if not providing `navLabel`
       * or `textButtonProps`
       */
      children: React.ReactNode;
    };

/**
 * Top nav for form pages. Accepts either a `navLabel` and `textButtonProps` or
 * `children` to render something custom.
 */
export const TopNav = (props: TopNavProps) => {
  const navLabel = 'navLabel' in props ? props.navLabel : undefined;
  const textButtonProps = 'textButtonProps' in props ? props.textButtonProps : undefined;
  const children = 'children' in props ? props.children : undefined;

  const { topBottom, rightLeft } = formLayoutTokens.margin.nav.top;

  return (
    <Box
      paddingVertical={topBottom.xsmallviewport}
      paddingHorizontal={rightLeft.xsmallviewport}
      $group-formLayout-largerThanXS={{
        paddingVertical: topBottom.smallviewport,
        paddingHorizontal: rightLeft.smallviewport,
      }}
      $group-formLayout-largerThanSM={{
        paddingVertical: topBottom.mediumviewport,
        paddingHorizontal: rightLeft.mediumviewport,
      }}
      $group-formLayout-largerThanMD={{
        paddingVertical: topBottom.largeviewport,
        paddingHorizontal: rightLeft.largeviewport,
      }}
      $group-formLayout-largerThanLG={{
        paddingVertical: topBottom.xlargeviewport,
        paddingHorizontal: rightLeft.xlargeviewport,
      }}
    >
      {children ? (
        children
      ) : (
        <Box flexDirection="row" justifyContent="space-between" alignItems="center">
          <Box>
            {navLabel && (
              <Text
                tag="span"
                typography={formLayoutTokens.typography.nav.top.address}
                color={formLayoutTokens.color.content.nav.top.addressLabel}
              >
                {navLabel}
              </Text>
            )}
          </Box>
          <Box>
            {textButtonProps?.label && (
              <TextButton size="small" variant="primary" {...textButtonProps} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
